import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import { scenePatchBody } from '@/infrastructure/api/v1/mappers/scenes';

export class PublishedStoryDurationService {
  /**
   * Calculate scene instance duration in milliseconds based on asset instances
   * Similar to StoryDurationService but specifically for a single scene instance
   * @param sceneInstanceId - The scene instance ID
   * @returns Duration in milliseconds, or 0 if no data found
   */
  async calculateSceneInstanceDuration(sceneInstanceId: string): Promise<number> {
    try {
      // Every asset instance of the scene: the generic route's or(neq ...) filter here was always true
      const { data, error: assetError } = await listAll(page => v1.GET('/api/v1/asset-instances', {
        params: { query: { sceneUniqueId: [sceneInstanceId], ...page } },
      }));
      const assetInstances = data?.map(assetInstanceToLegacy);

      if (assetError) {
        console.error('Error fetching asset instances for duration calculation:', assetError);
        return 0;
      }

      if (!assetInstances || assetInstances.length === 0) {
        console.warn(`No asset instances found for scene instance ${sceneInstanceId}`);
        return 0;
      }

      // Find the latest end time from asset instances
      const maxEndTime = Math.max(...assetInstances.map((asset) => asset.asset_instance_end_time || 0));

      // Return duration in milliseconds (asset_instance_end_time is already in milliseconds)
      return maxEndTime;
    } catch (error) {
      console.error('Error calculating scene instance duration:', error);
      return 0;
    }
  }

  /**
   * Update the scene_instance_duration_ms field in the database
   * @param sceneInstanceId - The scene instance ID
   * @param durationMs - Duration in milliseconds
   * @returns Success status
   */
  async updateSceneInstanceDuration(sceneInstanceId: string, durationMs: number): Promise<boolean> {
    try {
      const { error } = await toResult(v1.PATCH('/api/v1/scenes/{sceneUniqueId}', {
        params: { path: { sceneUniqueId: sceneInstanceId } },
        body: scenePatchBody({ scene_instance_duration_ms: durationMs }),
      }));

      if (error) {
        console.error('Error updating scene instance duration:', error);
        return false;
      }

      console.log(`Successfully updated scene instance ${sceneInstanceId} duration to ${durationMs}ms`);
      return true;
    } catch (error) {
      console.error('Error updating scene instance duration:', error);
      return false;
    }
  }

  /**
   * Calculate and update scene instance duration in one operation
   * This is the main method to call when publishing a scene
   * @param sceneInstanceId - The scene instance ID
   * @returns Success status and calculated duration
   */
  async calculateAndUpdateSceneInstanceDuration(sceneInstanceId: string): Promise<{
    success: boolean;
    durationMs: number;
  }> {
    try {
      // Calculate the duration
      const durationMs = await this.calculateSceneInstanceDuration(sceneInstanceId);
      
      // Update the database
      const updateSuccess = await this.updateSceneInstanceDuration(sceneInstanceId, durationMs);
      
      return {
        success: updateSuccess,
        durationMs
      };
    } catch (error) {
      console.error('Error in calculateAndUpdateSceneInstanceDuration:', error);
      return {
        success: false,
        durationMs: 0
      };
    }
  }

  /**
   * Format duration for display
   * @param durationMs - Duration in milliseconds
   * @returns Formatted duration string
   */
  formatDuration(durationMs: number): string {
    if (durationMs === 0) {
      return '0 min';
    }

    const durationMinutes = Math.ceil(durationMs / 60000);

    if (durationMinutes < 60) {
      return `${durationMinutes} min`;
    }

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
  }
}

